/* assets/mqb.js */
/* MQB — Multi-Quantity Bundle.
 *
 * Quantity-tier bundles ("Buy 2, Get 1 Free") for a single product, built to
 * plug into the same staging pipeline as MPB:
 *
 *   unit 0 (paid)      -> written into the product form's input[name="id"], so the
 *                         theme's buy-buttons picks it up as the bundle PARENT
 *   units 1..paid-1    -> staged here, added as sibling lines
 *   free units         -> staged here, flagged _FreeGift
 *
 * product-block-buy-buttons.js pops everything via window.MQB.getStagedLineItems(),
 * tags the group with one bundleId, and POSTs a single /cart/add.js. The cart
 * transform Function then merges the group into one line and applies
 * `bundleDiscountPct` so the free units come off the price.
 *
 * The discount has to be expressed as a percentage because Shopify's linesMerge
 * prices the merged line as the SUM of its components -- there is no way to zero
 * out an individual child line. See buildDiscountPct().
 */
(function () {
  "use strict";

  // ---------- Shared colour map (also populated by mpb.js; merge, never clobber) ----------
  window.MPB_COLOR_HEX = Object.assign({}, window.MPB_COLOR_HEX || {});
  const HEX = {};
  Object.keys(window.MPB_COLOR_HEX).forEach((k) => {
    HEX[String(k).trim().toLowerCase()] = window.MPB_COLOR_HEX[k];
  });

  function hexFor(name) {
    const key = String(name || "").trim().toLowerCase();
    if (HEX[key]) return HEX[key];
    // CSS named colours cover most of the rest ("red", "slate gray" -> "slategray")
    return key.replace(/\s+/g, "") || "#ccc";
  }

  /* Seasonal / patterned values (Cherie, La Lune) are IMAGE swatches with no entry in
   * the theme's hex map. hexFor() would return the value itself as a CSS colour --
   * `background: cherie` is invalid, which is why they rendered as blank white
   * circles. Paint the image when the payload has one. */
  function paintSwatch(el, value, swatches) {
    const data = (swatches && swatches[value]) || {};
    if (data.image) {
      el.style.backgroundImage = 'url("' + data.image + '")';
      el.style.backgroundSize = "cover";
      el.style.backgroundPosition = "center";
      el.style.backgroundColor = data.color || "transparent";
      return;
    }
    el.style.backgroundImage = "";
    el.style.backgroundColor = data.color || hexFor(value);
  }


  /* Convert shop-currency amounts with Shopify's published market rate before formatting
   * them in the shopper's currency; otherwise a $3 charm reads as A$3.00 in Australia. */
  function __presentmentRate() {
    try {
      const r = parseFloat((window.Shopify && window.Shopify.currency && window.Shopify.currency.rate) || "1");
      return isFinite(r) && r > 0 ? r : 1;
    } catch (e) { return 1; }
  }
  function money(cents) {
    const cur = (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || "USD";
    try {
      return new Intl.NumberFormat(undefined, {
        style: "currency",
        currency: cur,
        minimumFractionDigits: cents % 100 === 0 ? 0 : 2,
      }).format((cents / 100) * __presentmentRate());
    } catch (e) {
      return "$" + (cents / 100).toFixed(2);
    }
  }

  // ---------- Public staging API (mirrors window.MPB) ----------
  window.MQB = window.MQB || (function () {
    const map = new Map(); // bid -> items[]
    return {
      setSelections(bid, items) { map.set(String(bid), items || []); },
      getStagedLineItems(peek = false) {
        const flat = Array.from(map.values()).flat();
        if (!peek) map.clear();
        return flat;
      },
      // Re-stage after a failed add so the customer doesn't silently lose their picks
      restageLineItems(items) {
        if (Array.isArray(items) && items.length) map.set("__restaged", items);
      },
      /* Drive one unit's colour from outside — the charm modal picks the leash colour for
       * the set being personalised, and that set's unit has to move with it. */
      getUnitVariantIds() {
        const out = [];
        document.querySelectorAll(".mqb[data-bid]").forEach((r) => {
          if (typeof r.__mqbUnitVariantIds === "function") out.push(...r.__mqbUnitVariantIds());
        });
        return out;
      },
      setUnitColor(unitIndex, label) {
        let done = false;
        document.querySelectorAll(".mqb[data-bid]").forEach((r) => {
          if (!done && typeof r.__mqbSetUnitColor === "function") {
            done = r.__mqbSetUnitColor(unitIndex, label);
          }
        });
        return done;
      },
    };
  })();

  function readJSON(id, fallback) {
    const node = document.getElementById(id);
    if (!node) return fallback;
    try { return JSON.parse(node.textContent || "null") ?? fallback; }
    catch (e) { return fallback; }
  }

  function boot() {
    document.querySelectorAll(".mqb[data-bid]").forEach(init);
  }

  function init(root) {
    if (root.__mqbMounted) return;
    root.__mqbMounted = true;

    const bid = root.dataset.bid;
    const data = readJSON("mqb-payload-" + bid, null);
    const tiers = readJSON("mqb-tiers-" + bid, []);

    if (!data || !Array.isArray(data.variants) || !data.variants.length || !tiers.length) {
      root.hidden = true;
      window.MQB.setSelections(bid, []);
      return;
    }

    const swatchMap = data.colorSwatches || {};
    const variants = data.variants.filter((v) => v.available !== false);
    if (!variants.length) { root.hidden = true; return; }

    // Which option is the colour axis? Falls back to option 1.
    const optIndex = (function () {
      const names = (data.options || []).map((o) => String(o.name || "").toLowerCase());
      const i = names.findIndex((n) => n.includes("color") || n.includes("colour"));
      return i >= 0 ? i : 0;
    })();

    // Distinct swatch values on that axis, each mapped to a representative variant
    const swatches = [];
    const seen = new Set();
    variants.forEach((v) => {
      const val = (v.options || [])[optIndex];
      if (val == null || seen.has(val)) return;
      seen.add(val);
      swatches.push({ value: val, variant: v });
    });

    const form = findProductForm(root);
    const idInput = form ? form.querySelector('input[name="id"]') : null;

    // state: tierIndex + per-unit chosen variant id (paid units then free units)
    let tierIndex = Math.max(0, tiers.findIndex((t) => t.selected));
    if (tierIndex < 0) tierIndex = 0;
    const picks = new Map(); // "tierIndex:unitIndex" -> variant id

    /* Every unit defaults to this. Changing the colour in the theme's own picker
     * resets it and clears per-row overrides, so ALL rows follow the top selection
     * rather than just the first one. */
    let baseVariantId = null;
    let lastSeenId = null;
    function initialVariantId() {
      if (baseVariantId) return baseVariantId;
      if (idInput && idInput.value) {
        const hit = variants.find((v) => String(v.id) === String(idInput.value));
        if (hit) return hit.id;
      }
      return variants[0].id;
    }

    function pickFor(ti, ui) {
      const key = ti + ":" + ui;
      if (!picks.has(key)) picks.set(key, initialVariantId());
      return picks.get(key);
    }

    function variantById(id) {
      return variants.find((v) => String(v.id) === String(id)) || variants[0];
    }

    // ---------- Rendering ----------
    function render() {
      root.innerHTML = "";
      const list = document.createElement("div");
      list.className = "mqb__tiers";

      tiers.forEach((tier, ti) => {
        const paid = Math.max(1, parseInt(tier.paid, 10) || 1);
        const free = Math.max(0, parseInt(tier.free, 10) || 0);
        const isOpen = ti === tierIndex;

        /* Wrapper exists so the badge can straddle the card's top edge like a tab.
         * The card itself keeps `overflow: hidden` to clip the green free-gift bar to
         * its rounded corners, which would also clip a badge placed inside it. */
        const wrap = document.createElement("div");
        wrap.className = "mqb__tier-wrap" + (tier.badge ? " has-badge" : "");

        const card = document.createElement("div");
        card.className = "mqb__tier" + (isOpen ? " is-selected" : "");
        card.dataset.tier = String(ti);

        // --- header row: radio + label + price ---
        const head = document.createElement("label");
        head.className = "mqb__head";

        const radio = document.createElement("input");
        radio.type = "radio";
        radio.className = "mqb__radio";
        radio.name = "mqb-tier-" + bid;
        radio.checked = isOpen;
        radio.setAttribute("aria-label", tier.label || ("Buy " + paid));
        radio.addEventListener("change", () => { tierIndex = ti; render(); sync(); });

        const title = document.createElement("span");
        title.className = "mqb__title";
        title.textContent = tier.label || (free > 0 ? `Buy ${paid}, Get ${free} Free` : `Buy ${paid}`);

        const price = document.createElement("span");
        price.className = "mqb__price";
        price.textContent = money(tierTotal(ti, paid));

        head.append(radio, title, price);

        if (tier.badge) {
          const b = document.createElement("span");
          b.className = "mqb__badge";
          b.textContent = tier.badge;
          wrap.append(b);          // sits above the card, half-hidden behind its top edge
        }
        card.append(head);

        // --- expanded body: one picker row per paid unit ---
        if (isOpen && (paid > 1 || free > 0)) {
          const body = document.createElement("div");
          body.className = "mqb__body";
          for (let u = 0; u < paid; u++) body.append(unitRow(ti, u, false));
          card.append(body);
        }

        // --- free gift bar ---
        if (free > 0) {
          if (isOpen) {
            for (let f = 0; f < free; f++) card.append(freeRow(ti, paid + f, free, f));
          } else {
            const teaser = document.createElement("div");
            teaser.className = "mqb__free mqb__free--teaser";
            const img = thumb(variantById(pickFor(ti, 0)));
            const txt = document.createElement("span");
            txt.className = "mqb__free-label";
            txt.textContent = (free > 1 ? free + " FREE " : "FREE ") + (data.shortTitle || data.title).toUpperCase();
            teaser.append(img, txt);
            card.append(teaser);
          }
        }

        // Clicking anywhere on a collapsed card selects it
        card.addEventListener("click", (e) => {
          if (isOpen) return;
          if (e.target.closest("select") || e.target.closest(".mqb__swatch")) return;
          tierIndex = ti; render(); sync();
        });

        wrap.append(card);
        list.append(wrap);
      });

      root.append(list);
    }

    function thumb(v) {
      const img = document.createElement("img");
      img.className = "mqb__thumb";
      img.width = 48; img.height = 48;
      img.loading = "lazy";
      img.alt = "";
      img.src = v.image || data.featuredImage || "";
      return img;
    }

    function unitRow(ti, ui) {
      const row = document.createElement("div");
      row.className = "mqb__unit";
      const current = variantById(pickFor(ti, ui));

      row.append(thumb(current));

      const right = document.createElement("div");
      right.className = "mqb__unit-main";

      const lbl = document.createElement("div");
      lbl.className = "mqb__unit-label";
      const optName = ((data.options || [])[optIndex] || {}).name || "Color";
      lbl.innerHTML = `<b>${escapeHtml(optName)}</b> - <span class="mqb__unit-value">${escapeHtml((current.options || [])[optIndex] || current.title)}</span>`;
      right.append(lbl);

      const bar = document.createElement("div");
      bar.className = "mqb__swatches";
      swatches.forEach((s) => {
        const btn = document.createElement("button");
        btn.type = "button";
        btn.className = "mqb__swatch";
        paintSwatch(btn, s.value, swatchMap);
        btn.title = s.value;
        btn.setAttribute("aria-label", s.value);
        if ((current.options || [])[optIndex] === s.value) btn.classList.add("is-active");
        btn.addEventListener("click", (e) => {
          e.preventDefault(); e.stopPropagation();
          picks.set(ti + ":" + ui, s.variant.id);
          render(); sync();
        });
        bar.append(btn);
      });
      right.append(bar);
      row.append(right);
      return row;
    }

    function freeRow(ti, unitIndex, freeCount, freeOrdinal) {
      const wrap = document.createElement("div");
      wrap.className = "mqb__free";
      const current = variantById(pickFor(ti, unitIndex));
      wrap.append(thumb(current));

      const txt = document.createElement("span");
      txt.className = "mqb__free-label";
      txt.textContent = (freeCount > 1 ? "1 of " + freeCount + " FREE " : "1 FREE ") + (data.shortTitle || data.title);
      wrap.append(txt);

      const sel = document.createElement("select");
      sel.className = "mqb__free-select";
      sel.setAttribute("aria-label", "Choose your free item");
      swatches.forEach((s) => {
        const o = document.createElement("option");
        o.value = String(s.variant.id);
        o.textContent = s.value;
        if (String(s.variant.id) === String(current.id)) o.selected = true;
        sel.append(o);
      });
      sel.addEventListener("change", (e) => {
        e.stopPropagation();
        picks.set(ti + ":" + unitIndex, sel.value);
        render(); sync();
      });
      wrap.append(sel);
      return wrap;
    }

    // ---------- Pricing ----------
    function tierTotal(ti, paid) {
      let sum = 0;
      for (let u = 0; u < paid; u++) sum += variantById(pickFor(ti, u)).price || 0;
      return sum;
    }

    /* linesMerge prices the merged line as the SUM of every component line, so a
     * "free" unit cannot simply be zero-rated. Express the giveaway as the
     * percentage that takes the full sum down to the paid-only sum. */
    function buildDiscountPct(ti, paid, free) {
      let paidSum = 0, allSum = 0;
      for (let u = 0; u < paid; u++) { const p = variantById(pickFor(ti, u)).price || 0; paidSum += p; allSum += p; }
      for (let f = 0; f < free; f++) allSum += variantById(pickFor(ti, paid + f)).price || 0;
      if (allSum <= 0 || allSum === paidSum) return 0;
      return Math.max(0, Math.min(100, (1 - paidSum / allSum) * 100));
    }

    // ---------- Staging ----------
    function sync() {
      const tier = tiers[tierIndex] || {};
      const paid = Math.max(1, parseInt(tier.paid, 10) || 1);
      const free = Math.max(0, parseInt(tier.free, 10) || 0);

      // Unit 0 becomes the main line item / bundle parent.
      const parentId = pickFor(tierIndex, 0);
      if (idInput && String(idInput.value) !== String(parentId)) {
        /* Set the value only. Dispatching `change` here made the theme's variant
         * selector re-render the product section, detaching this block's DOM and
         * orphaning the staged items. buy-buttons reads the form at submit time, so
         * a silent write is enough. */
        idInput.value = String(parentId);
      }
      /* Record it either way. Without this the mirror below sees MQB's own write as a
       * customer changing the theme picker and resets every row — so changing one row
       * inside the offer changed them all. */
      if (idInput) lastSeenId = String(idInput.value);

      // A single-unit tier is an ordinary product purchase -- stage nothing so
      // buy-buttons falls through to its normal add path.
      if (paid === 1 && free === 0) {
        const q = form ? form.querySelector('[name="quantity"]') : null;
        if (q) q.value = "1";   // clear a quantity a multi-unit tier may have set
        const ghost = form ? form.querySelector('[data-mqb-quantity]') : null;
        if (ghost) ghost.value = "1";
        window.MQB.setSelections(bid, []);
        return;
      }

      /* Two ways to make the free units free, chosen per-block:
       *
       *  'auto'      (default, works on every Shopify plan) -- the units are added as
       *              ordinary cart lines and a native "Buy X get Y" AUTOMATIC DISCOUNT
       *              zeroes them. No Function involved, so we must NOT emit
       *              bundleDiscountPct or the discount would be applied twice if a
       *              cart transform is ever enabled.
       *
       *  'transform' (Shopify Plus, or a public app) -- the cart transform Function
       *              merges the group and applies bundleDiscountPct. linesMerge prices
       *              the merged line as the SUM of its components, so the giveaway has
       *              to be expressed as a percentage off that sum.
       */
      const mode = (root.dataset.discountMode || "auto").toLowerCase();
      const props = { _BundleTier: tier.label || `Buy ${paid}` };
      /* 'mirror': the free units ARE a different product — the $0 mirror, same SKU, real
       * price in compare_at — so nothing has to discount them and nothing has to stay
       * ungrouped. That is what lets the cart attach a charm set to the leash it belongs to
       * instead of piling every set onto one line. Falls back to 'auto' for any colour the
       * mirror does not carry, so a missing variant charges rather than adds the wrong one. */
      const mirrorMode = mode === "mirror";
      const mirrorFor = (variantId) => {
        const m = (data.mirror || {})[String(variantId)];
        return m && m.id && m.available !== false ? String(m.id) : "";
      };
      if (mode === "transform") {
        props.bundleDiscountPct = buildDiscountPct(tierIndex, paid, free).toFixed(6);
      } else {
        // Tells buy-buttons to leave these lines ungrouped so the native automatic
        // discount can still see the qualifying quantities. Leading underscore keeps
        // it hidden from the cart and order display.
        props._MQBStandalone = "1";
      }

      /* Paid units are expressed as QUANTITY, not as extra staged lines.
       *
       * Shopify only merges cart lines when their variant AND properties match. Staging
       * unit 2 with properties the form's main item doesn't have produced two separate
       * "x1" rows for what is really one product bought twice. So: count the paid units
       * per variant, put the count for unit 0's variant on the form's quantity input,
       * and stage only the ones the customer changed to a DIFFERENT variant (which
       * genuinely are different lines). */
      const paidByVariant = new Map();
      for (let u = 0; u < paid; u++) {
        const id = String(pickFor(tierIndex, u));
        paidByVariant.set(id, (paidByVariant.get(id) || 0) + 1);
      }

      const mainId = String(parentId);
      /* Not every product template renders a quantity selector. Without one there is no
       * [name="quantity"] to drive, and the paid units would fall back to separate
       * single-quantity lines — so inject a hidden one. buy-buttons reads the form via
       * FormData at submit, so a hidden field counts just the same. */
      let qtyInput = form ? form.querySelector('[name="quantity"]') : null;
      if (!qtyInput && form) {
        qtyInput = document.createElement("input");
        qtyInput.type = "hidden";
        qtyInput.name = "quantity";
        qtyInput.setAttribute("data-mqb-quantity", "1");
        form.appendChild(qtyInput);
      }
      if (qtyInput) qtyInput.value = String(paidByVariant.get(mainId) || 1);

      const items = [];
      paidByVariant.forEach((qty, id) => {
        if (id === mainId) return;            // carried by the form's own line
        items.push({ id, quantity: qty, properties: { ...props } });
      });

      /* Free units stay their own line, aggregated by variant, so the customer can see
       * what they are getting for nothing. */
      const freeByVariant = new Map();
      for (let f = 0; f < free; f++) {
        const chosen = String(pickFor(tierIndex, paid + f));
        const id = mirrorMode ? (mirrorFor(chosen) || chosen) : chosen;
        freeByVariant.set(id, (freeByVariant.get(id) || 0) + 1);
      }
      freeByVariant.forEach((qty, id) => {
        const p = { ...props, _FreeGift: "1" };
        /* A $0 mirror needs no discount and no standalone guard: grouping it is safe and is
         * the whole point. Only the auto-discount shape has to stay ungrouped. */
        if (mirrorMode) delete p._MQBStandalone;
        items.push({ id, quantity: qty, properties: p });
      });

      window.MQB.setSelections(bid, items);
    }

    /* Mirror the theme's own variant picker into unit 0.
     * Polling as well as listening: the theme re-renders the variant selector on
     * change, which replaces the radios and loses any listener bound to them, and
     * `input[name="id"]` is a hidden input that never fires `change` on its own. */
    /* Set unit N to the colour named by `label`, wrapping when there are more charm sets
     * than units — the same index-and-repeat rule the staged charm bands use. */
    /* The unit variants of the open tier, in order — paid first, then free. buy-buttons
     * needs this to attach charm set N to the leash at position N. */
    root.__mqbUnitVariantIds = function () {
      const tier = tiers[tierIndex] || {};
      const paid = Math.max(1, parseInt(tier.paid, 10) || 1);
      const free = Math.max(0, parseInt(tier.free, 10) || 0);
      const out = [];
      for (let u = 0; u < paid + free; u++) out.push(String(pickFor(tierIndex, u)));
      return out;
    };

    root.__mqbSetUnitColor = function (unitIndex, label) {
      const want = String(label || "").trim().toLowerCase();
      if (!want) return false;
      const hit = swatches.find((s) => String(s.value).trim().toLowerCase() === want);
      if (!hit) return false;
      const tier = tiers[tierIndex] || {};
      const paid = Math.max(1, parseInt(tier.paid, 10) || 1);
      const free = Math.max(0, parseInt(tier.free, 10) || 0);
      const units = paid + free;
      if (units <= 0) return false;
      const n = Number(unitIndex);
      const ui = (((Number.isFinite(n) ? n : 0) % units) + units) % units;
      picks.set(tierIndex + ":" + ui, hit.variant.id);
      render(); sync();
      return true;
    };

    lastSeenId = idInput ? String(idInput.value) : null;
    function adoptFormVariant() {
      const el = (form || document).querySelector('input[name="id"]');
      if (!el || !el.value) return;
      // Only an EXTERNAL change counts: sync() keeps lastSeenId in step with our writes.
      if (String(el.value) === String(lastSeenId)) return;
      lastSeenId = String(el.value);
      const hit = variants.find((v) => String(v.id) === String(el.value));
      if (!hit) return;
      // Theme picker changed -> drive every unit, in every tier.
      baseVariantId = hit.id;
      picks.clear();
      render();
      sync();
    }
    if (form) form.addEventListener("change", adoptFormVariant);
    document.addEventListener("variant:change", adoptFormVariant);
    setInterval(adoptFormVariant, 400);

    /* Re-stage immediately before add-to-cart. buy-buttons peeks at
     * window.MQB.getStagedLineItems() the moment the button is clicked; running in the
     * capture phase guarantees the map is current no matter what else has touched the
     * DOM since the last render. */
    document.addEventListener("click", (e) => {
      const t = e.target && e.target.closest
        ? e.target.closest('button[type="submit"], [js-add-to-cart], [data-add-to-cart], .product-form__submit')
        : null;
      if (!t) return;
      sync();
    }, true);

    render();
    sync();
  }

  function findProductForm(root) {
    const section = root.closest(".shopify-section, [id^='shopify-section-']") || document;
    return (
      section.querySelector('form[data-type="add-to-cart-form"]') ||
      section.querySelector('form[action*="/cart/add"]') ||
      document.querySelector('form[data-type="add-to-cart-form"]') ||
      document.querySelector('form[action*="/cart/add"]')
    );
  }

  function escapeHtml(s) {
    return String(s).replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c]));
  }

  ["DOMContentLoaded", "shopify:section:load", "shopify:section:select"].forEach((e) =>
    document.addEventListener(e, boot)
  );
  if (document.readyState !== "loading") boot();
})();
